import React, { useState } from 'react';
import { MessageSquare, ClipboardCopy, CheckSquare, RotateCcw, Check, Loader2 } from 'lucide-react';

const BASE = process.env.REACT_APP_API_URL || '';

export default function SmartActionsBar({ clientId, clientName, event, carrier, onNewCase, onSmsSent }) {
  const [copying, setCopying] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [taskCreated, setTaskCreated] = useState(false);
  const [showSms, setShowSms] = useState(false);

  if (!event) return null;

  const reasonLabel = (event.call_reason || '').replace(/_/g, ' ');
  const outcomeLabel = (event.outcome || '').replace(/_/g, ' ');
  const carrierName = event.carrier_name || carrier?.carrier_name || 'Carrier';

  // Build case summary text
  function buildSummary() {
    const lines = [
      `Service Case Summary — ${clientName}`,
      `Date: ${new Date(event.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`,
      `Carrier: ${carrierName}`,
      `Issue: ${reasonLabel}`,
    ];
    if (event.call_reason_detail) lines.push(`Details: ${event.call_reason_detail}`);
    if (event.outcome) lines.push(`Outcome: ${outcomeLabel}`);
    if (event.reference_number) lines.push(`Ref #: ${event.reference_number}`);
    if (event.outcome_notes) lines.push(`Notes: ${event.outcome_notes}`);
    if (event.requires_follow_up && event.follow_up_date) {
      lines.push(`Follow-up: ${new Date(event.follow_up_date).toLocaleDateString()}`);
      if (event.follow_up_reason) lines.push(`Follow-up reason: ${event.follow_up_reason}`);
    }
    if (event.call_duration_seconds) lines.push(`Call duration: ${Math.round(event.call_duration_seconds / 60)}m`);
    return lines.join('\n');
  }

  // Copy summary to clipboard
  async function handleCopySummary() {
    try {
      setCopying(true);
      await navigator.clipboard.writeText(buildSummary());
      setTimeout(() => setCopying(false), 2000);
    } catch {
      setCopying(false);
    }
  }

  // Create follow-up task
  async function handleCreateTask() {
    setCreatingTask(true);
    try {
      const followUpDate = event.follow_up_date
        ? new Date(event.follow_up_date).toISOString()
        : new Date(Date.now() + 3 * 86400000).toISOString();

      const res = await fetch(`${BASE}/tasks/clients/${clientId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: `Follow up with ${carrierName} re: ${reasonLabel}${event.reference_number ? ` (Ref #${event.reference_number})` : ''}`,
          notes: event.outcome_notes || event.call_reason_detail || '',
          priority: event.outcome === 'escalated' ? 'high' : 'normal',
          dueDate: followUpDate,
          taskType: 'follow_up',
        }),
      });

      if (res.ok) {
        setTaskCreated(true);
        window.dispatchEvent(new CustomEvent('tasks:update'));
      }
    } catch (err) {
      console.error('Failed to create task:', err);
    } finally {
      setCreatingTask(false);
    }
  }

  // Pre-compose SMS message
  function getSmsMessage() {
    const name = (clientName || '').split(' ')[0] || 'there';
    let msg = `Hi ${name}, I called ${carrierName} about your ${reasonLabel}.`;
    if (event.outcome === 'resolved') {
      msg += ' The issue has been resolved.';
    } else if (event.outcome === 'pending') {
      msg += ' It is still being processed.';
    } else if (event.outcome === 'escalated') {
      msg += ' I escalated it to a supervisor for further review.';
    } else if (event.outcome === 'callback_scheduled') {
      msg += ' They will be calling back.';
    }
    if (event.reference_number) msg += ` Ref #${event.reference_number}.`;
    if (event.follow_up_date) {
      msg += ` I'll follow up on ${new Date(event.follow_up_date).toLocaleDateString()}.`;
    }
    msg += ' Let me know if you have any questions!';
    return msg;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Quick Actions</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {/* Text Client Update */}
        <button
          onClick={() => { setShowSms(true); if (onSmsSent) onSmsSent(getSmsMessage()); }}
          className="flex flex-col items-center gap-1.5 px-3 py-3 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-center"
        >
          <MessageSquare size={18} className="text-indigo-500" />
          <span className="text-[11px] font-medium text-slate-700">Text Client</span>
        </button>

        {/* Create Follow-Up Task */}
        <button
          onClick={handleCreateTask}
          disabled={creatingTask || taskCreated}
          className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-lg border transition-colors text-center ${
            taskCreated
              ? 'border-emerald-300 bg-emerald-50'
              : 'border-slate-200 hover:border-blue-300 hover:bg-blue-50'
          }`}
        >
          {creatingTask ? (
            <Loader2 size={18} className="text-blue-500 animate-spin" />
          ) : taskCreated ? (
            <Check size={18} className="text-emerald-500" />
          ) : (
            <CheckSquare size={18} className="text-blue-500" />
          )}
          <span className="text-[11px] font-medium text-slate-700">
            {taskCreated ? 'Task Created' : 'Create Task'}
          </span>
        </button>

        {/* Copy Summary */}
        <button
          onClick={handleCopySummary}
          className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-lg border transition-colors text-center ${
            copying
              ? 'border-emerald-300 bg-emerald-50'
              : 'border-slate-200 hover:border-purple-300 hover:bg-purple-50'
          }`}
        >
          {copying ? (
            <Check size={18} className="text-emerald-500" />
          ) : (
            <ClipboardCopy size={18} className="text-purple-500" />
          )}
          <span className="text-[11px] font-medium text-slate-700">
            {copying ? 'Copied!' : 'Copy Summary'}
          </span>
        </button>

        {/* New Case */}
        <button
          onClick={onNewCase}
          className="flex flex-col items-center gap-1.5 px-3 py-3 rounded-lg border border-slate-200 hover:border-amber-300 hover:bg-amber-50 transition-colors text-center"
        >
          <RotateCcw size={18} className="text-amber-500" />
          <span className="text-[11px] font-medium text-slate-700">New Case</span>
        </button>
      </div>
    </div>
  );
}
